import React, { useEffect, useState } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { services } from '../data/mock';
import useScrollAnimation from '../hooks/useScrollAnimation';

const Services = () => {
  const [activeService, setActiveService] = useState(services[0]?.id ?? '');
  const currentService = services.find((s) => s.id === activeService) || services[0];
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.15 });
  const contentRevealClass = isVisible ? 'service-content-row' : 'service-content-row-hidden';

  useEffect(() => {
    const handleServiceSelection = (event) => {
      const selectedServiceId = event?.detail?.serviceId;
      if (!selectedServiceId) {
        return;
      }

      const exists = services.some((service) => service.id === selectedServiceId);
      if (exists) {
        setActiveService(selectedServiceId);
      }
    };

    window.addEventListener('synentia:select-service', handleServiceSelection);
    return () => window.removeEventListener('synentia:select-service', handleServiceSelection);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const serviceSlug = params.get('service');
    if (!serviceSlug) {
      return;
    }

    const matchedService = services.find((service) => service.slug === serviceSlug);
    if (matchedService) {
      setActiveService(matchedService.id);
    }
  }, []);

  return (
    <section id="services" className="relative pt-10 sm:pt-14 lg:pt-16 pb-20 sm:pb-24 lg:pb-32 bg-[--bg-primary]">
      <div className="absolute top-0 left-0 right-0 section-divider" />
      
      <div ref={sectionRef} className="max-w-[1500px] mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div 
          className={`mb-16 max-w-4xl transition-all duration-700 ease-out ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
        >
          <p className="section-eyebrow mb-4">Capabilities</p>
          <h2 className="text-[clamp(2rem,4.2vw,4rem)] font-semibold text-[--text-primary] leading-[1.02]">
            From strategic advice to
            <br />
            <span className="accent-wash">full operational delivery.</span>
          </h2>
          <p className="mt-6 text-lg text-[--text-secondary] max-w-3xl">
            Choose your starting point. We handle the architecture, implementation, and adoption
            {' '}— until AI is embedded in how you work.
          </p>
        </div>

        {/* Services Navigation */}
        <div className="grid lg:grid-cols-[340px_1fr] gap-10 xl:gap-14">
          <div
            className={`lg:hidden transition-all duration-700 ease-out delay-150 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
            }`}
          >
            <label
              htmlFor="service-select"
              className="block text-[11px] font-mono text-[--text-muted] uppercase tracking-[0.18em] mb-2"
            >
              Select Service
            </label>
            <div className="relative">
              <select
                id="service-select"
                value={activeService}
                onChange={(event) => setActiveService(event.target.value)}
                className="w-full appearance-none rounded-xl border border-[--border] bg-[--bg-secondary] text-[--text-primary] text-sm py-3 pl-4 pr-11 focus:outline-none focus:ring-2 focus:ring-[--accent]"
              >
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[--text-muted]" />
            </div>
          </div>

          {/* Left - Service Tabs */}
          <div 
            className={`hidden lg:block lg:space-y-2 transition-all duration-700 ease-out delay-200 ${
              isVisible ? 'translate-x-0 opacity-100' : '-translate-x-8 opacity-0'
            }`}
          >
            {services.map((service, index) => (
              <button
                key={service.id}
                onClick={() => setActiveService(service.id)}
                className={`w-full text-left p-5 rounded-2xl transition-all duration-300 group flex items-center gap-4 ${
                  activeService === service.id
                    ? 'soft-card'
                    : 'hover:bg-[--bg-secondary] border border-transparent'
                }`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <span className={`text-[11px] font-mono ${
                  activeService === service.id ? 'text-[--accent]' : 'text-[--text-muted]'
                }`}>
                  {service.id}
                </span>
                <span className={`flex-1 text-sm font-medium leading-snug ${
                  activeService === service.id ? 'text-[--text-primary]' : 'text-[--text-secondary]'
                }`}>
                  {service.label}
                </span>
                <ArrowRight className={`w-4 h-4 ml-auto transition-all ${
                  activeService === service.id 
                    ? 'text-[--accent] translate-x-0 opacity-100' 
                    : 'text-[--text-muted] -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'
                }`} />
              </button>
            ))}
          </div>

          {/* Right - Service Detail */}
          <div 
            className={`relative overflow-hidden soft-card rounded-3xl p-6 sm:p-8 lg:p-14 min-h-[460px] sm:min-h-[520px] transition-all duration-700 ease-out delay-400 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
            }`}
          >
            <div className="service-panel-ambient" />
            <div key={activeService} className="relative z-10 service-content-in">
              <div className={`flex items-center gap-3 mb-7 ${contentRevealClass}`} style={{ '--service-delay': '40ms' }}>
                <span className="text-[11px] font-mono text-[--bg-primary] bg-[--accent] px-3 py-1 rounded-full">
                  {currentService?.id}
                </span>
                <span className="text-[11px] font-mono text-[--text-muted] uppercase tracking-[0.17em]">
                  {currentService?.label}
                </span>
              </div>
              
              <h3
                className={`text-[1.85rem] sm:text-3xl lg:text-[2.6rem] font-semibold text-[--text-primary] mb-6 leading-tight ${contentRevealClass}`}
                style={{ '--service-delay': '90ms' }}
              >
                {currentService?.title}
              </h3>
              
              <p
                className={`text-[--text-secondary] text-base sm:text-lg leading-relaxed mb-10 max-w-3xl ${contentRevealClass}`}
                style={{ '--service-delay': '140ms' }}
              >
                {currentService?.description}
              </p>

              <div className="grid sm:grid-cols-2 gap-4 lg:gap-5 mb-10">
                {currentService?.highlights?.map((item, index) => (
                  <div
                    key={`${currentService?.id}-${item.title}`}
                    className={`service-highlight-card ${contentRevealClass}`}
                    style={{ '--service-delay': `${210 + index * 80}ms` }}
                  >
                    <p className="text-[13px] font-mono uppercase tracking-[0.12em] text-[--accent]">
                      {item.title}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-[--text-secondary]">
                      {item.detail}
                    </p>
                  </div>
                ))}
              </div>

              <a 
                href={currentService?.link}
                className={`inline-flex items-center gap-2 px-6 py-3 text-[11px] font-medium text-[--bg-primary] uppercase tracking-[0.18em] font-mono bg-[--accent] rounded-full hover:bg-[--accent-hover] transition-all btn-pattern ${contentRevealClass}`}
                style={{ '--service-delay': '430ms' }}
              >
                Learn more
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
